import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { getProviders, deleteProvider, restoreProvider } from "api/Providers";
import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { PlusIcon, QuestionMarkIcon, TrashIcon, CheckMarkIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import moment from "moment-timezone";
import AvatarIcon from "components/svg/icons/AvatarIcon";
import Span from "components/typography/Span";
import { Badge, Box, Flex, Image } from "theme-ui";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";


export default function Providers () {
  const navigate = useNavigate();

  const columnHelper = createColumnHelper();
  const [refreshData, setRefreshData] = useState<boolean>(false);  

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const lastTrackedQuery = useRef<string | null>(null);

  const columns = [  
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => {
        const provider = info.row.original;
        const isDeleted = Boolean(provider?.deletedAt) || provider?.status === 0;
        const cellContent = (
          <Flex sx={{gap: "1rem", alignItems : "center", cursor: isDeleted ? "default" : "pointer"}} onClick={() => {
            if (!isDeleted) {
              navigate(`/providers/${provider.id}`);
            }
          }}>
          {(info.row.original?.logo) ?
            <Image width="40px" height="40px" src={(info.row.original?.logo)} />
          :
            <AvatarIcon fill={"#BFBFBF"} width={"40px"}/>
          }           
          <Box>
            <Span color="#000" fontWeight="500">{info.getValue()}</Span>
            <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original?.contactEmail}</Span>         
          </Box>
        </Flex>
        );
        return cellContent;
      },
      header: () => <span>Provider Info</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.depotsCount, {
      id: "depots",
      cell: (info: any) => info.getValue(),
      header: () => <span>Depots</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info) => {
        switch (info.getValue()) {
          case 1:
            return <Badge as="span" variant="success">Active</Badge>;
          default:
            return <Badge as="span" variant="danger">Inactive</Badge>;
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row.updatedAt, {
      id: "updatedAt",
      cell: (info) => moment(info.getValue()).format('MMM D, YYYY'),
      header: () => <span>Last Updated</span>,
    }),     
    columnHelper.accessor((row: any) => row.createdAt, {
      id: "createdAt",
      cell: (info) => moment(info.getValue()).format('MMM D, YYYY'),
      header: () => <span>Date Added</span>,
    }),  
    columnHelper.accessor((row: any) => row.lastUpdateBy, {
      id: "lastUpdateBy",
      cell: (info) => `${info?.getValue()?.firstName || ""} ${info?.getValue()?.lastName || ""}`,
      header: () => <span>Updated By</span>,
    }), 
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const provider = info.row.original;
        const isDeleted = Boolean(provider?.deletedAt) || provider?.status === 0;
        const items = [
          ...(!isDeleted
            ? [
                {
                  label: "Edit",
                  onClick: () => navigate(`/providers/${provider.id}/edit`),
                },
              ]
            : []),
          ...(isDeleted
            ? [
                {
                  label: "Reactivate",
                  onClick: () => submitReactivate(info.row.original),
                  style: { color: themevals.colors.success },
                },
              ]
            : []),
          ...(!isDeleted
            ? [
                {
                  label: "Deactivate",
                  onClick: () => submitDelete(info.row.original),
                  style: { color: "var(--theme-ui-colors-danger)" },
                },
              ]
            : []),
        ];

        return (
          <Box>          
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />          
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];               

const confirmDelete = async (del:any) => { 
  setConfirmSubmit(true);   
  const res = await deleteProvider({ data: del });
  if(res?.success){ 
    toast.add("Provider deactivated.","var(--theme-ui-colors-success)");
      void trackActivity({
        module: "providers",
        action: "deactivate_provider",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "provider",
        entityId: del?.id ?? null,
        metadata: {
          source: "provider_list",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deleting provider.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const confirmReactivate = async (provider: any) => {
    setConfirmSubmit(true);
    try {
      const res = await restoreProvider({ id: provider?.id });
      if (res?.success) {
        toast.add("Provider reactivated.","var(--theme-ui-colors-success)");
        void trackActivity({
          module: "providers",
          action: "reactivate_provider",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "provider",
          entityId: provider?.id ?? null,
          metadata: null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setRefreshData(true);
        setTimeout(() => { setRefreshData(false); }, 100);
      } else {
        toast.add(res?.message ? res.message : "Error reactivating provider.","var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message ? error.message : "Error reactivating provider.","var(--theme-ui-colors-danger)");
    } finally {
      setConfirmSubmit(false);
      setConfirmModal(false);
    }
  };

  const submitDelete = async (del?:any) => {
    setConfirmModal(<BasicModal
      title="Deactivate provider?"    
      cancel_click={() => setConfirmModal(false)}
      cancel_text="No, cancel"   
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete(del)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Deactivate"}
        </BasicButton>
      </>}    
    >
      <P>Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{del?.name}</strong>?</P>
    </BasicModal>);
  }

  const submitReactivate = async (provider: any) => {
    setConfirmModal(
      <BasicModal
        title="Reactivate provider?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        $submitting={confirmSubmit}
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
            <BasicButton
              type="button"
              onClick={() => confirmReactivate(provider)}
              $submitting={confirmSubmit}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{
                margin: "0",
                backgroundColor: themevals.colors.success,
                borderColor: themevals.colors.success,
                color: "#fff",
              }}
            >
              {!confirmSubmit && <CheckMarkIcon fill={"#fff"} />}
              Reactivate
            </BasicButton>
        }
      >
        <P>
          Are you sure you want to reactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{provider?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Providers"        
  }, []); 

  return (
    <>
      <Header></Header>      
      <Page title="Providers" subtitle="Manage your provider network.">                
        <TableData 
          api={getProviders} 
          columns={columns} 
          refresh={refreshData} 
          title={"All Providers"}
          returnQuery={(query: any) => {
            if (!query) return;

            const rawFilters = query?.filters ?? {};
            const cleanedFilters = Object.entries(rawFilters).reduce<Record<string, string>>((acc, [key, value]) => {
              if (value === undefined || value === null || value === "" || value === ":null") {
                return acc;
              }
              acc[key] = value as string;
              return acc;
            }, {});

            const signature = JSON.stringify({
              page: query?.pageNum ?? null,
              limit: query?.recordsPer ?? null,
              filters: cleanedFilters,
            });

            if (lastTrackedQuery.current === signature) {
              return;
            }
            lastTrackedQuery.current = signature;

            void trackActivity({
              module: "providers",
              action: "view_provider_list",
              actionType: "view",
              userId: user?.id || null,
              companyId: user?.company?.id || null,
              entityType: "provider_list",
              entityId: null,
              metadata: {
                page: query?.pageNum ?? null,
                limit: query?.recordsPer ?? null,
                filters: Object.keys(cleanedFilters).length > 0 ? cleanedFilters : {},
              },
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
          }}
          actions={[<BasicButton key="1" onClick={() => navigate('/providers/create')} $iconHeight="10px"><PlusIcon fill="#fff"/>Add provider</BasicButton>]} 
        />
      </Page>      
      {confirmModal}
    </>
  );
}
